import { useLayoutEffect, useRef } from 'react';

import {
  autoUpdate,
  computePosition,
  Middleware,
  offset,
  shift,
} from '@floating-ui/dom';
import { useDeferredElement } from '@cast/utils';

import {
  TOOLTIP_DEFAULT_SKIDDING,
  TOOLTIP_SCREEN_PADDING,
} from 'core/tour-v2/constants';

import { useTourV2 } from '../TourProvider';

import { TourStepTooltipConfig } from './types';
import { Box } from '@cast/design-system';

type Props = {
  config: TourStepTooltipConfig;
};

const properVerticalPosition: Middleware = {
  name: 'properVerticalPosition',
  fn({ x, y, placement, rects: { reference } }) {
    const isLeft = placement.startsWith('left');
    const isRight = placement.startsWith('right');
    const isVertical = isLeft || isRight;
    const isStart = placement.endsWith('start');
    const isEnd = placement.endsWith('end');

    const yShift =
      // position vertical tooltips on top or under the referenced element
      isVertical
        ? isStart
          ? reference.height + TOOLTIP_DEFAULT_SKIDDING
          : isEnd
          ? -reference.height - TOOLTIP_DEFAULT_SKIDDING
          : 0
        : 0;

    return {
      x,
      y: y + yShift,
    };
  },
};

export function TourTooltip(props: Props) {
  const tooltipElement = useRef<HTMLElement>(undefined);
  const anchorElement = useDeferredElement(
    props.config.anchor,
    props.config.continuouslyWatch
  );

  useLayoutEffect(() => {
    if (anchorElement) {
      // ensure the anchor is visible and there is a room for tooltip as well
      anchorElement.scrollIntoView({ block: 'center' });
    }
  }, [anchorElement]);

  useLayoutEffect(() => {
    const tooltip = tooltipElement.current;
    if (!anchorElement || !tooltip) {
      return;
    }

    props.config.onAttached?.();

    return autoUpdate(anchorElement, tooltip, () =>
      computePosition(anchorElement, tooltip, {
        placement: props.config.placement,
        strategy: 'absolute',
        middleware: [
          offset({
            mainAxis: props.config.distance ?? 0,
            crossAxis: props.config.offset ?? 0,
          }),
          properVerticalPosition,
          shift({ padding: TOOLTIP_SCREEN_PADDING }),
        ],
      }).then(({ x, y }) => {
        Object.assign(tooltip.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      })
    );
  }, [anchorElement, props.config]);

  const tour = useTourV2();
  const showTooltip: boolean = !!anchorElement;

  if (!showTooltip) {
    return null;
  }

  return (
    <Box
      ref={tooltipElement}
      sx={{
        position: 'absolute',
      }}
      className="tour-tooltip"
    >
      {typeof props.config.content === 'function'
        ? props.config.content(tour.api)
        : props.config.content}
    </Box>
  );
}
